import { ChangeEvent, FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';
// i18next
import { useTranslation } from 'react-i18next';
// Hooks
import useQuestionInterval from 'hooks/useQuestionInterval';
// Redux
import { useAppDispatch, useAppSelector } from 'hooks/useStore';
import { setQuestionReady, setQuestionStart, setAnswers, setQuestionEnd } from 'store/player/playerSlice';
import { selectQuestion, selectQuestionIndex, selectQuestionsLength, selectQuiz } from 'store/quiz/quizSelectors';
import { selectQuestionEnd } from 'store/player/playerSelectors';
// Components
import { Button } from 'components/Buttons';
import { Input } from 'components/Controls';
import QuestionScoringBar from './QuestionScoringBar';
// Utilities
import { findBestMatchItem } from 'utilities/Utilities';
import { fontStack } from 'utilities/fontLoader';

type FeedbackStatus = 'correct' | 'incorrect' | 'duplicate';

interface Feedback {
  id: number;
  status: FeedbackStatus;
  guess: string;
  ref?: string;
}

const FEEDBACK_DURATION = 3000;

const FEEDBACK_LABELS: Record<FeedbackStatus, string> = {
  correct: 'Correct!',
  incorrect: 'Incorrect!',
  duplicate: 'Already guessed!',
};

const FEEDBACK_ICONS: Record<FeedbackStatus, string> = {
  correct: '✓',
  incorrect: '✕',
  duplicate: '↺',
};

const TopList:FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  // State
  const quiz = useAppSelector(selectQuiz);
  const question = useAppSelector(selectQuestion);
  const questionEnd = useAppSelector(selectQuestionEnd);
  const questionsLength = useAppSelector(selectQuestionsLength);
  const currentQuestionIndex = useAppSelector(selectQuestionIndex);

  const items = question.config?.items ?? [];

  const [title, setTitle] = useState('');
  const [guessedAnswers, setGuessedAnswers] = useState<{ref:string, title: string}[]>([]);
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const guessedRefs = new Set(guessedAnswers.map(a => a.ref));
  const score = items.reduce((acc, item) => {
    return guessedRefs.has(item.ref)
      ? acc + item.scoring.points
      : acc;
  }, 0);

  const onChange = (e:ChangeEvent<HTMLInputElement>) => {
    setTitle(e.target.value);
  };

  const { width, time } = useQuestionInterval();

  const onSubmit = () => {
    const value = title.trim();
    if (!value) return;

    const result = findBestMatchItem(value, items, guessedAnswers, 2);

    if (result) {
      setGuessedAnswers(prev => [
        ...prev,
        { ref: result.item.ref, title: result.matchedAnswer }
      ]);
      setFeedback({ id: Date.now(), status: 'correct', guess: value, ref: result.item.ref });
    } else {
      // Distinguish a repeated correct answer from a wrong one
      const alreadyGuessed = findBestMatchItem(value, items.filter(item => guessedRefs.has(item.ref)), [], 2);
      setFeedback(alreadyGuessed
        ? { id: Date.now(), status: 'duplicate', guess: value, ref: alreadyGuessed.item.ref }
        : { id: Date.now(), status: 'incorrect', guess: value }
      );
    }

    setTitle('');
  };

  // Auto-dismiss feedback
  useEffect(() => {
    if (!feedback) return;
    const timeout = setTimeout(() => setFeedback(null), FEEDBACK_DURATION);
    return () => clearTimeout(timeout);
  }, [feedback]);

  // Scroll the list to the matched row
  useEffect(() => {
    if (!feedback?.ref) return;
    rowRefs.current[feedback.ref]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [feedback]);

  useEffect(() => {
    dispatch(setQuestionReady(true));
    dispatch(setQuestionStart(true));
    // eslint-disable-next-line
  }, []);

  const skipQuestion = useCallback(() => {
    dispatch(setQuestionStart(false));
    dispatch(setQuestionEnd(true));
  }, [dispatch]);

  useEffect(() => {
    if (guessedAnswers.length === items.length) skipQuestion();
  }, [guessedAnswers, items.length, skipQuestion]);

  useEffect(() => {
    return () => {
      if ((!questionEnd && guessedAnswers.length !== items.length) || guessedAnswers.length === 0 || !questionEnd) return;

      dispatch(setAnswers([{
        type: question.type,
        input: {
          items: guessedAnswers.map(item => ({
            ref: item.ref,
            text: item.title,
          }))
        },
        title: '',
        score,
        time,
        correct: true,
        question: {
          ref: question.ref,
          title: question.title
        }
      }] as any));
    };
    // eslint-disable-next-line
  }, [guessedAnswers, questionEnd]);

  const onKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !questionEnd) {
      onSubmit();
    }
  };

  const correctColor = quiz.questionsSettings.correctAnswerColor;
  const incorrectColor = quiz.questionsSettings.incorrectAnswerColor;

  const feedbackColor = feedback?.status === 'correct'
    ? correctColor
    : feedback?.status === 'incorrect' ? incorrectColor : '#6b7280';

  return (
    <div className="flex-1 px-4" style={{ fontFamily: fontStack(quiz.questionsSettings.font, quiz.fonts) }}>
      <div
        className="mb-4 p-1 shadow-primary-border rounded-xl"
        style={{ backgroundColor: quiz.theme.contentBackgroundColor }}
      >
        <p className="text-base text-primary font-bold text-center">
          {question.title}
        </p>
      </div>

      <div className="flex gap-4 mb-4">
        <div className="flex-grow">
          {question.config?.items?.map((item, index) => {
            const guessedAnswer = guessedAnswers.some(answer => answer.ref === item.ref);
            const isLatest = feedback?.ref === item.ref;
            return (
              <div
                key={isLatest ? `${item.ref}-${feedback.id}` : item.ref}
                ref={el => { rowRefs.current[item.ref] = el; }}
                className={`py-1 px-2 mb-2 rounded-md flex items-center justify-between gap-2 last:mb-0 ${isLatest ? 'top-list-row-new' : ''}`}
                style={{ backgroundColor: guessedAnswer ? correctColor : '#fff', scrollMarginBottom: 120 }}
              >
                <p className="font-bold">{guessedAnswer || questionEnd ? item.correctAnswers[0] : index + 1}</p>
                <p className={`text-sm ${guessedAnswer ? 'text-white' : 'text-gray-500'}`}>{`${item.scoring.points}pts`}</p>
              </div>
            )
          })}
        </div>
        <QuestionScoringBar width={width} />
      </div>

      <p className="text-primary text-right text-sm font-bold">
        {currentQuestionIndex}&nbsp;/&nbsp;{questionsLength}
      </p>

      {questionEnd && guessedAnswers.length > 0 ? (
        <p className="mt-4 text-2xl text-primary text-center font-bold">
          {`Congratulations! You got ${guessedAnswers.length} right and scored ${score} points!`}
        </p>
      ) : (
        <Fragment>
          <p className="mt-4 text-2xl text-primary text-center font-bold">Type your answer, then hit Go</p>
          <div className="mb-4 py-3 sticky bottom-0 z-10">
            <div
              aria-live="polite"
              role="status"
              className="absolute bottom-full inset-x-0 flex justify-center pointer-events-none"
            >
              {feedback && (
                <div
                  key={feedback.id}
                  className="top-list-feedback max-w-full py-1 px-3 rounded-full text-white text-sm shadow-lg flex items-center gap-1.5"
                  style={{ backgroundColor: feedbackColor }}
                >
                  <span className="font-bold shrink-0" aria-hidden="true">{FEEDBACK_ICONS[feedback.status]}</span>
                  <span className="font-bold shrink-0">{FEEDBACK_LABELS[feedback.status]}</span>
                  <span className="truncate">{feedback.guess}</span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-4">
              <div className="w-full">
                <Input
                  className="-mb-4 flex-grow"
                  id="top-list-answer-input"
                  name="top-list-answer-input"
                  value={title}
                  onChange={onChange}
                  disabled={questionEnd}
                  onKeyPress={onKeyPress}
                />
              </div>
              <Button
                className="capitalize primary-button"
                onClick={onSubmit}
                disabled={!title.trim() || questionEnd}
              >{t('buttons.go')}</Button>
            </div>
          </div>
          <Button
            className="w-full text-xl uppercase primary-button"
            onClick={skipQuestion}
            disabled={questionEnd}
          >
            {t('buttons.passQuestion')}
          </Button>
        </Fragment>
      )}
    </div>
  );
}

export default TopList;
